import { forEach } from 'lodash';
import { types } from 'mobx-state-tree';

import FieldType from '../types/FieldType';
import FormField from './FormField';

/*
 * A Form is used to keep the data for a form in the UI that has multiple fields.
 * Each field should have its own FormField.
 */
const Form = types
  .model('Form', {
    fields: types.optional(types.map(FormField), {}),
    name: types.optional(types.string, 'GeneralForm'),
  })
  .views((self) => {
    return {
      getField(field) {
        return self.fields.get(field);
      },
      /**
       * By default instansiates all fields
       * @param {Set<FormFieldType>} invalidFieldsTypes
       * @returns {Array<FormField>} instansiated fields
       */
      getFormFields(invalidFieldTypes = null) {
        return [...self.fields.values()].filter(
          (field) => !invalidFieldTypes.has(field.type) ?? true
        );
      },
      get fieldsArray() {
        return Array.from(self.fields, ([key, value]) => value);
      },
      get hasFields() {
        return self.fields.size > 0;
      },
      get valid() {
        let valid = true;
        // We use lodash forEach instead of for ... of for IE 11 compatibility
        forEach(self.fieldsArray, (field) => {
          if (field.active && field.valid === null) {
            valid = null;
            return false; // break
          }
          if (field.active && field.valid === false) {
            valid = false;
            return false; // break
          }
        });
        return valid;
      },
    };
  })
  .actions((self) => {
    return {
      getFieldValues: () => {
        const values = {};
        self.fields.forEach((field, key) => {
          // File fields only use the value for validation.
          if (field.active && field.type === FieldType.NORMAL) {
            values[key] = field.value;
          }
        });
        return values;
      },
      setField(fieldName, field) {
        self.fields.set(fieldName, field);
      },
      setFields(fields) {
        Object.keys(fields).forEach((fieldName) => {
          self.fields.set(fieldName, fields[fieldName]);
        });
      },
      removeField(field) {
        self.fields.delete(field);
      },
      validateField(fieldName) {
        return self.fields.get(fieldName).setShouldValidate(true);
      },
      validate() {
        return self.fields.forEach((field) => field.setShouldValidate(true));
      },
    };
  });

export default Form;

export function createForm(fields, name) {
  return Form.create({
    fields,
    name,
  });
}
